/**
 * The "add people or teams" search box and its results dropdown.
 *
 * Extracted from ShareModal so the default-sharing setting picks its audience
 * the same way sharing one entity does (#11877). Presentational: it owns the
 * query and whether the dropdown is open, nothing else.
 */

import React from "react";
import { MagnifyingGlass, UsersThree } from "@phosphor-icons/react";
import {
  getInitials,
  type PickableTeam,
  type PickableUser,
} from "./shareParts";

interface GranteePickerProps<U extends PickableUser, T extends PickableTeam> {
  users: U[];
  teams: T[];
  /** How many other people and teams exist at all, for the empty-state copy. */
  otherMemberCount: number;
  /** True when the roster could not be loaded, which is not an empty roster. */
  statusFailed?: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  showResults: boolean;
  onShowResultsChange: (show: boolean) => void;
  onAddUser: (user: U) => void;
  onAddTeam: (team: T) => void;
}

const GranteePicker = <U extends PickableUser, T extends PickableTeam>({
  users,
  teams,
  otherMemberCount,
  statusFailed = false,
  query,
  onQueryChange,
  showResults,
  onShowResultsChange,
  onAddUser,
  onAddTeam,
}: GranteePickerProps<U, T>) => {
  const isEmpty = users.length === 0 && teams.length === 0;

  return (
    <div className="relative">
      <div className="relative">
        <MagnifyingGlass
          size={18}
          weight="regular"
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--fp-ink-4)]"
        />
        <input
          type="text"
          placeholder="Add people or teams..."
          value={query}
          onChange={(e) => {
            onQueryChange(e.target.value);
            onShowResultsChange(true);
          }}
          onFocus={() => onShowResultsChange(true)}
          onBlur={() => onShowResultsChange(false)}
          className="w-full pl-10 pr-4 py-2 rounded-md text-sm bg-[var(--fp-surface)] text-[var(--fp-ink)] placeholder:text-[var(--fp-ink-4)] border border-[var(--fp-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--fp-ink-3)] focus:border-[var(--fp-ink-3)] transition-colors"
        />
      </div>

      {/* An empty result set still opens the dropdown with an explanation —
          silently rendering nothing read as a broken picker when an org has no
          one else to share with. The explanation must not overstate what we
          know: a failed status fetch says so, and an org whose members are all
          already shared with is not an empty org. */}
      {showResults && isEmpty && (
        <div
          // Keeps the input focused (so onBlur never fires) rather than
          // racing a blur timer; there's nothing clickable in this empty
          // state, but the handler stays consistent with the results panel.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute top-full left-0 right-0 mt-1 bg-[var(--fp-surface)] border border-[var(--fp-border)] rounded-lg [box-shadow:var(--fp-lift-overlay)] z-10 px-4 py-3 text-sm text-[var(--fp-ink-3)]"
        >
          {statusFailed
            ? "Couldn't load sharing status"
            : query.trim()
              ? "No results found"
              : otherMemberCount > 0
                ? "Everyone in your organization already has access"
                : "No other members in your organization"}
        </div>
      )}
      {showResults && !isEmpty && (
        <div
          // Keeps the input focused (so onBlur never fires) when a result is
          // clicked, instead of racing a blur timer against the click.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute top-full left-0 right-0 mt-1 bg-[var(--fp-surface)] border border-[var(--fp-border)] rounded-lg [box-shadow:var(--fp-lift-overlay)] max-h-60 overflow-y-auto z-10 fp-scroll-quiet"
        >
          {users.map((user) => (
            <div
              key={user.id}
              className="flex items-start p-3 hover:bg-[var(--fp-band-hover)] cursor-pointer border-b border-[var(--fp-border)] last:border-b-0"
              onClick={() => onAddUser(user)}
            >
              <div className="w-8 h-8 bg-[var(--fp-band)] rounded-full flex items-center justify-center text-sm font-medium text-[var(--fp-ink-2)] mr-3 flex-shrink-0">
                {getInitials(user.name)}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-[var(--fp-ink)]">
                  {user.name}
                </span>
                {user.email && (
                  <span className="text-xs text-[var(--fp-ink-3)] mt-0.5">
                    {user.email}
                  </span>
                )}
              </div>
            </div>
          ))}
          {teams.map((team) => (
            <div
              key={team.id}
              className="flex items-center p-3 hover:bg-[var(--fp-band-hover)] cursor-pointer border-b border-[var(--fp-border)] last:border-b-0"
              onClick={() => onAddTeam(team)}
            >
              <div className="w-8 h-8 bg-[var(--fp-ink)] rounded-full flex items-center justify-center text-[var(--fp-surface)] mr-3">
                <UsersThree size={16} weight="regular" />
              </div>
              <span className="text-sm font-medium text-[var(--fp-ink)]">
                {team.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GranteePicker;
